// Maps a free-text proposal topic (Claude returns whatever it finds:
// "Construction", "Mass transit", "Standards Commissioner", …) to a
// fixed set of broad policy categories so the public view can compare
// parties side-by-side per category.
//
// Categories are intentionally standard manifesto-style buckets — the
// kind of headings a reader recognises from any election coverage —
// rather than creative re-framings. We aim for ~7 buckets so each one
// is broad enough to absorb related proposals, plus an "Other"
// catch-all for things that don't fit cleanly.
//
// First-match wins: order from most-specific to most-broad.

export interface Category {
  key: string;
  label: string;
  icon: string;
  keywords: string[];
}

export const CATEGORIES: Category[] = [
  {
    key: 'health',
    label: 'Health & Welfare',
    icon: '🏥',
    keywords: [
      'health', 'healthcare', 'hospital', 'hospitals',
      'mental health', 'wellbeing', 'wellness',
      'elderly', 'care', 'caregiver',
      'medicine', 'medical', 'medicines',
      'doctor', 'doctors', 'nurses', 'pharma',
      'mater dei', 'gozo general', 'st luke',
      'welfare', 'benefit', 'benefits', 'social welfare',
      'disability', 'disabled', 'accessibility',
    ],
  },
  {
    key: 'education',
    label: 'Education',
    icon: '🎓',
    keywords: [
      'education', 'school', 'schools', 'schooling',
      'university', 'um ', 'tertiary',
      'student', 'students', 'stipend', 'stipends',
      'youth', 'children', 'child',
      'training', 'skills', 'apprenticeship',
      'teacher', 'teachers', 'lecturer',
    ],
  },
  {
    key: 'environment',
    label: 'Environment & Climate',
    icon: '🌍',
    keywords: [
      'environment', 'environmental',
      'climate', 'climate change',
      'green', 'sustainability', 'sustainable',
      'energy', 'renewable', 'solar', 'wind',
      'pollution', 'air quality',
      'waste', 'recycling', 'recycle',
      'biodiversity', 'nature',
      'water', 'sea', 'coast', 'coastline',
    ],
  },
  {
    key: 'governance',
    label: 'Justice & Governance',
    icon: '⚖️',
    keywords: [
      'governance', 'government',
      'rule of law', 'rule-of-law',
      'corruption', 'anti-corruption',
      'standards', 'commissioner',
      'judiciary', 'court', 'judge',
      'parliament', 'mp ', 'minister',
      'transparency', 'foi ',
      'integrity', 'whistleblower',
      'electoral', 'election reform',
      'democracy', 'institution', 'institutions',
      'crime', 'police',
      'justice', 'sentencing', 'prison',
      'drugs', 'drug',
      'domestic violence', 'femicide',
      'gang', 'violence', 'safety',
    ],
  },
  {
    key: 'society',
    label: 'Society & Culture',
    icon: '🤝',
    keywords: [
      'culture', 'cultural', 'arts',
      'sport', 'sports', 'football',
      'heritage',
      'tourism', 'tourist',
      'language', 'maltese language', 'malti',
      'immigration', 'migrant', 'integration', 'foreign worker',
      'lgbt', 'lgbtiq', 'gender',
      'equality', 'inequality',
      'inclusion', 'minority', 'minorities',
      'religion', 'faith',
      'family', 'families', 'parental',
    ],
  },
  {
    key: 'infrastructure',
    label: 'Housing & Infrastructure',
    icon: '🏗️',
    keywords: [
      'housing', 'house', 'home', 'homes',
      'rent', 'rental', 'tenant',
      'construction', 'odz', 'planning', 'permit', 'pa ',
      'property', 'real estate',
      'first-time buyer', 'first time buyer',
      'transport', 'transportation', 'mobility',
      'traffic', 'congestion',
      'road', 'roads', 'bus', 'buses', 'metro', 'tram',
      'cycling', 'cyclist', 'bicycle',
      'infrastructure', 'mass transit', 'ferry', 'port', 'airport',
    ],
  },
  {
    key: 'economy',
    label: 'Economy & Jobs',
    icon: '💼',
    keywords: [
      'economy', 'economic',
      'tax', 'taxes', 'taxation',
      'jobs', 'job', 'wage', 'wages', 'salary', 'salaries',
      'business', 'businesses', 'sme',
      'investment', 'investments', 'investor',
      'pension', 'pensions',
      'income', 'finance', 'financial', 'banking', 'industry',
      'cost of living', 'inflation',
      'food', 'grocery',
    ],
  },
  {
    key: 'other',
    label: 'Other',
    icon: '📌',
    keywords: [], // catch-all
  },
];

/** Returns the category key for a free-text topic. Falls back to 'other'. */
export function categoryFor(topic: string | null | undefined): string {
  const t = (topic ?? '').toLowerCase();
  if (!t) return 'other';
  for (const cat of CATEGORIES) {
    if (cat.key === 'other') continue;
    if (cat.keywords.some((kw) => t.includes(kw))) return cat.key;
  }
  return 'other';
}

export function categoryMeta(key: string): Category {
  return CATEGORIES.find((c) => c.key === key) ?? CATEGORIES[CATEGORIES.length - 1];
}
